// WebSocket client (spec §15.3): exponential backoff with jitter, resume from last event id,
// ref-counted channel subscriptions, connection status for the polling fallback.

export interface RealtimeEvent {
  id: number;
  channel: string;
  event: string;
  payload: any;
  ts: string;
  replay?: boolean;
}

type Handler = (e: RealtimeEvent) => void;
export type ConnStatus = 'connecting' | 'open' | 'closed';

function matches(pattern: string, channel: string) {
  return pattern === channel || (pattern.endsWith('*') && channel.startsWith(pattern.slice(0, -1)));
}

class RealtimeClient {
  private ws: WebSocket | null = null;
  private token: string | null = null;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private lastEventId: number | undefined;
  private subs = new Map<string, number>();
  private handlers = new Set<{ channels: string[]; fn: Handler }>();
  private statusListeners = new Set<(s: ConnStatus) => void>();
  status: ConnStatus = 'closed';
  baseMs = 1000;
  maxMs = 30_000;

  connect(token: string) {
    if (this.token === token && this.ws) return;
    this.token = token;
    this.open();
  }

  disconnect() {
    this.token = null;
    if (this.timer) clearTimeout(this.timer);
    this.ws?.close();
    this.ws = null;
    this.setStatus('closed');
  }

  private setStatus(s: ConnStatus) {
    this.status = s;
    this.statusListeners.forEach((l) => l(s));
  }

  private open() {
    if (!this.token) return;
    this.ws?.close();
    this.setStatus('connecting');
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/ws?token=${encodeURIComponent(this.token)}`);
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.setStatus('open');
      this.sendSubscribe([...this.subs.keys()]);
    };
    ws.onmessage = (m) => {
      let msg: any;
      try {
        msg = JSON.parse(m.data);
      } catch {
        return;
      }
      if (msg.type !== 'event') return;
      if (typeof msg.id === 'number') this.lastEventId = Math.max(this.lastEventId ?? 0, msg.id);
      for (const h of this.handlers) if (h.channels.some((c) => matches(c, msg.channel))) h.fn(msg);
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.setStatus('closed');
      if (ev.code === 4401 || !this.token) return; // unauthorised: wait for a new token
      const delay = Math.min(this.maxMs, this.baseMs * 2 ** this.attempt++) * (0.75 + Math.random() * 0.5);
      this.timer = setTimeout(() => this.open(), delay);
    };
    ws.onerror = () => ws.close();
  }

  private sendSubscribe(channels: string[]) {
    if (this.ws?.readyState === WebSocket.OPEN && channels.length) {
      this.ws.send(JSON.stringify({ type: 'subscribe', channels, lastEventId: this.lastEventId }));
    }
  }

  subscribe(channels: string[], fn: Handler): () => void {
    const entry = { channels, fn };
    this.handlers.add(entry);
    const added = channels.filter((c) => {
      const n = this.subs.get(c) ?? 0;
      this.subs.set(c, n + 1);
      return n === 0;
    });
    if (added.length && this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'subscribe', channels: added }));
    return () => {
      this.handlers.delete(entry);
      const removed = channels.filter((c) => {
        const n = (this.subs.get(c) ?? 1) - 1;
        if (n <= 0) this.subs.delete(c);
        else this.subs.set(c, n);
        return n <= 0;
      });
      if (removed.length && this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'unsubscribe', channels: removed }));
    };
  }

  onStatus(fn: (s: ConnStatus) => void): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }
}

export const realtime = new RealtimeClient();
